import React from "react";
import { motion } from "framer-motion";
import { Check, AlertCircle } from "lucide-react";

/**
 * The small pieces every onboarding action needs so nothing ever happens silently:
 * a button that shows it is working, a confirmation before the screen changes, and
 * an error that explains itself.
 */

export function Spinner({ size = 14, className = "" }) {
  return (
    <span
      role="status"
      aria-label="Working"
      className={`inline-block rounded-full border-2 border-current border-r-transparent align-[-2px] motion-safe:animate-spin ${className}`}
      style={{ width: size, height: size }}
    />
  );
}

/**
 * A button that cannot be pressed twice and always says what it is doing.
 * `busy` swaps the icon for a spinner and the label for `busyLabel`.
 */
export function ActionButton({
  children,
  busyLabel,
  busy = false,
  disabled = false,
  icon: Icon,
  variant = "primary",
  className = "",
  ...rest
}) {
  const base =
    "inline-flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-bold rounded-xl " +
    "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 " +
    "focus-visible:ring-green-600 disabled:cursor-not-allowed";
  const variants = {
    primary: "bg-green-600 text-white hover:bg-green-700 disabled:bg-green-600/40",
    quiet:
      "border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50 " +
      "disabled:hover:bg-transparent",
    danger:
      "border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50 " +
      "disabled:hover:bg-transparent",
  };

  return (
    <motion.button
      type="button"
      whileTap={busy || disabled ? undefined : { scale: 0.97 }}
      disabled={busy || disabled}
      aria-busy={busy}
      className={`${base} ${variants[variant]} ${className}`}
      {...rest}
    >
      {busy ? <Spinner /> : Icon ? <Icon size={13} strokeWidth={3} /> : null}
      {busy && busyLabel ? busyLabel : children}
    </motion.button>
  );
}

/** Shown in place of a form once its action succeeded, before the state moves on. */
export function Confirmation({ title, detail }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex items-start gap-4 rounded-2xl border border-green-200 bg-green-50/70 px-6 py-5"
    >
      <motion.span
        initial={{ scale: 0.6 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 420, damping: 18 }}
        className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-green-600 text-white"
      >
        <Check size={15} strokeWidth={3.5} />
      </motion.span>
      <div className="min-w-0">
        <p className="text-sm font-bold text-slate-900">{title}</p>
        {detail && <p className="mt-1 text-sm leading-relaxed text-slate-600">{detail}</p>}
      </div>
    </motion.div>
  );
}

export function ErrorNote({ children }) {
  if (!children) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      role="alert"
      className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3"
    >
      <AlertCircle size={15} className="mt-0.5 shrink-0 text-red-600" />
      <p className="text-sm font-medium leading-relaxed text-red-800">{children}</p>
    </motion.div>
  );
}

/** Placeholder rows that hold the layout steady while the gate loads. */
export function Skeleton({ className = "" }) {
  return (
    <div
      className={`motion-safe:animate-pulse rounded-xl bg-slate-200/70 ${className}`}
      aria-hidden="true"
    />
  );
}
